import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const contactInfo = [
  {
    icon: MapPin,
    title: "Dirección",
    details: ["Nicanor Molinare 3228", "Macul, Región Metropolitana", "Chile"]
  },
  {
    icon: Phone,
    title: "Teléfono",
    details: ["+56 2 2345 6789", "+56 9 8765 4321"]
  },
  {
    icon: Mail,
    title: "Email",
    details: ["contacto@mycing.cl"]
  }
];

const contactSchema = z.object({
  name: z.string()
    .min(2, "El nombre debe tener al menos 2 caracteres")
    .max(100, "El nombre es demasiado largo"),
  email: z.string()
    .email("Email inválido")
    .max(255, "El email es demasiado largo"),
  phone: z.string()
    .min(8, "El teléfono debe tener al menos 8 dígitos")
    .max(20, "El teléfono es demasiado largo"),
  message: z.string()
    .min(10, "El mensaje debe tener al menos 10 caracteres")
    .max(1000, "El mensaje es demasiado largo"),
  captcha: z.string()
    .min(1, "Por favor resuelve el CAPTCHA")
});

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // CAPTCHA matemático simple
  const [captchaNum1] = useState(Math.floor(Math.random() * 10) + 1);
  const [captchaNum2] = useState(Math.floor(Math.random() * 10) + 1);
  const captchaAnswer = captchaNum1 + captchaNum2;

  const form = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      captcha: ""
    }
  });

  const onSubmit = async (values: z.infer<typeof contactSchema>) => {
    // Validar CAPTCHA
    if (parseInt(values.captcha) !== captchaAnswer) {
      toast({
        title: "Error en CAPTCHA",
        description: "La respuesta del CAPTCHA es incorrecta",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phone: values.phone,
          message: values.message
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "¡Mensaje enviado!",
          description: "Nos pondremos en contacto contigo pronto.",
        });
        form.reset();
      } else {
        throw new Error(data.error || 'Error al enviar');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo enviar el mensaje. Intenta nuevamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 relative overflow-hidden bg-gradient-to-br from-dark-section via-dark-section/95 to-industrial-gray">
      {/* Background Effects Premium */}
      <div className="absolute inset-0 opacity-30" style={{ background: 'var(--gradient-mesh)' }} />
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-liquid-blob" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/20 rounded-full blur-3xl animate-liquid-blob" style={{ animationDelay: '3s' }} />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold mb-6 text-balance text-white">
            CONTÁCTANOS
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto font-inter leading-relaxed">
            Estamos listos para ayudarte con tu proyecto
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8 animate-fade-in-left">
            <h3 className="text-2xl font-montserrat font-bold mb-8 text-white">
              Información de Contacto
            </h3>
            {contactInfo.map((item, index) => (
              <div 
                key={index} 
                className="flex items-start group"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl glass-liquid-medium bg-primary/20 backdrop-blur-xl border border-white/20 flex items-center justify-center mr-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-liquid-lg">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h4 className="font-montserrat font-bold text-lg mb-2 text-white group-hover:text-primary transition-colors">
                    {item.title}
                  </h4>
                  {item.details.map((detail, idx) => (
                    <p key={idx} className="text-white/70 font-inter">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Mapa Satelital con Calles */}
            <div className="mt-8 rounded-2xl overflow-hidden border-2 border-white/20 shadow-liquid-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1665.797!2d-70.5988!3d-33.4871!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9662cf6a99999999%3A0x1234567890abcdef!2sNicanor%20Molinare%203228%2C%20Macul!5e1!4m2!3m1!1s0x0%3A0x0"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación MYCING - Nicanor Molinare 3228, Macul"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-fade-in-right">
            <div className="backdrop-blur-2xl bg-white/10 border-2 border-white/20 p-8 md:p-12 rounded-3xl shadow-liquid-2xl">
              <h3 className="text-xl font-montserrat font-bold mb-6 text-white">Envíanos un Mensaje</h3>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  {/* Nombre */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="Nombre completo" 
                            {...field}
                            className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                        </FormControl>
                        <FormMessage className="text-red-300" />
                      </FormItem>
                    )}
                  />
                  
                  {/* Email */}
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            type="email"
                            placeholder="Email" 
                            {...field}
                            className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                        </FormControl>
                        <FormMessage className="text-red-300" />
                      </FormItem>
                    )}
                  />
                  
                  {/* Teléfono */}
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="Teléfono" 
                            {...field}
                            className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                        </FormControl>
                        <FormMessage className="text-red-300" />
                      </FormItem>
                    )}
                  />
                  
                  {/* Mensaje */}
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea 
                            placeholder="Mensaje" 
                            rows={5}
                            {...field}
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-primary focus:ring-2 focus:ring-primary/50"
                          />
                        </FormControl>
                        <FormMessage className="text-red-300" />
                      </FormItem>
                    )}
                  />

                  {/* CAPTCHA Matemático */}
                  <FormField
                    control={form.control}
                    name="captcha"
                    render={({ field }) => (
                      <FormItem>
                        <div className="backdrop-blur-xl bg-white/10 border-2 border-white/30 p-4 rounded-2xl">
                          <p className="text-white font-montserrat font-bold mb-2 text-center">
                            Verifica que eres humano
                          </p>
                          <p className="text-white/90 font-inter text-center mb-3 text-lg">
                            ¿Cuánto es <span className="font-bold text-primary">{captchaNum1}</span> + <span className="font-bold text-primary">{captchaNum2}</span>?
                          </p>
                          <FormControl>
                            <Input 
                              placeholder="Tu respuesta" 
                              type="number"
                              {...field}
                              className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 text-center focus:border-primary focus:ring-2 focus:ring-primary/50"
                            />
                          </FormControl>
                        </div>
                        <FormMessage className="text-red-300" />
                      </FormItem>
                    )}
                  />
                  
                  {/* Botón Submit */}
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 glass-liquid-medium hover-liquid font-montserrat font-semibold text-base shadow-liquid-lg hover:shadow-liquid-xl rounded-2xl group backdrop-blur-xl bg-primary/90 text-white disabled:opacity-50 disabled:cursor-not-allowed" 
                  >
                    {isSubmitting ? (
                      <>ENVIANDO...</>
                    ) : (
                      <>
                        ENVIAR MENSAJE
                        <Mail className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
